import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";

// Widget chatbot iniettato dinamicamente dallo script salvato in
// Gestione Utenti → Pannello Chatbot. L'admin può cambiare il widget
// senza redeploy.
//
// - SOLO per utenti loggati con `role !== "admin"`.
// - NON su rotte pubbliche / kiosk: /login, /auth/callback,
//   /reset-password, /feedback-form, /monitor.
// - Lo script raw incollato dall'admin viene "sandbox-parsato": estraiamo
//   `src` + `data-*` e re-iniettiamo un <script> nostro per evitare di
//   eseguire codice inline arbitrario.

const SCRIPT_ATTR = "data-bdd-chatbot";
const API = process.env.REACT_APP_BACKEND_URL;
const EXCLUDED_PATHS = [
  "/login",
  "/auth/callback",
  "/reset-password",
  "/feedback-form",
  "/monitor",
];

const HIDE_STYLE_ID = "bdd-chatbot-hide-style";
function ensureHideStyle() {
  if (document.getElementById(HIDE_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = HIDE_STYLE_ID;
  style.textContent = `
    body.bdd-no-chatbot lc-text-widget,
    body.bdd-no-chatbot [id*="lc_text-widget"],
    body.bdd-no-chatbot [class*="lc_text-widget"],
    body.bdd-no-chatbot [class*="lc-chat-widget"],
    body.bdd-no-chatbot leadconnector-chat-widget {
      display: none !important;
      visibility: hidden !important;
    }
  `;
  document.head.appendChild(style);
}

// Estrae attributi rilevanti da uno snippet <script src="..." data-*="...">.
// Accetta anche tag senza chiusura o con apici misti.
function parseScriptTag(snippet) {
  if (!snippet || typeof snippet !== "string") return null;
  const trimmed = snippet.trim();
  if (!trimmed) return null;
  // Match della prima coppia di <script ...></script> o <script .../>.
  const m = trimmed.match(/<script\b([^>]*)>([\s\S]*?)<\/script>|<script\b([^>]*)\/?>/i);
  if (!m) return null;
  const attrs = m[1] || m[3] || "";
  const out = { src: "", dataAttrs: {} };
  // Estrai src
  const srcMatch = attrs.match(/\bsrc\s*=\s*"([^"]+)"|\bsrc\s*=\s*'([^']+)'/i);
  if (srcMatch) out.src = srcMatch[1] || srcMatch[2] || "";
  // Estrai tutti i data-*
  const dataRegex = /\b(data-[\w-]+)\s*=\s*"([^"]*)"|\b(data-[\w-]+)\s*=\s*'([^']*)'/gi;
  let match;
  while ((match = dataRegex.exec(attrs)) !== null) {
    const name = match[1] || match[3];
    const val = match[2] || match[4] || "";
    out.dataAttrs[name] = val;
  }
  return out.src ? out : null;
}

function removeWidget() {
  document.querySelectorAll(`script[${SCRIPT_ATTR}]`).forEach((el) => el.remove());
  document.body.classList.add("bdd-no-chatbot");
}

function addWidget(snippet) {
  const parsed = parseScriptTag(snippet);
  if (!parsed) {
    removeWidget();
    return;
  }
  document.body.classList.remove("bdd-no-chatbot");
  // Idempotente: se lo stesso src/widget-id è già montato, niente da fare.
  const existing = document.querySelector(`script[${SCRIPT_ATTR}]`);
  const newWidgetId = parsed.dataAttrs["data-widget-id"] || "";
  if (existing && existing.getAttribute("data-widget-id") === newWidgetId && existing.src === parsed.src) {
    return;
  }
  // Reset: rimuovi vecchi widget prima di iniettare il nuovo.
  document.querySelectorAll(`script[${SCRIPT_ATTR}]`).forEach((el) => el.remove());
  const s = document.createElement("script");
  s.src = parsed.src;
  Object.entries(parsed.dataAttrs).forEach(([k, v]) => s.setAttribute(k, v));
  s.setAttribute(SCRIPT_ATTR, "1");
  s.async = true;
  document.body.appendChild(s);
}

export default function ChatbotWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [config, setConfig] = useState(null);

  // Fetch della configurazione corrente (solo se l'utente è loggato).
  // La cache è semplice: ogni cambio di user → refetch.
  useEffect(() => {
    let cancelled = false;
    if (!user) { setConfig(null); return; }
    axios.get(`${API}/api/auth/me/chatbot-config`, { withCredentials: true })
      .then((res) => { if (!cancelled) setConfig(res.data || null); })
      .catch(() => { if (!cancelled) setConfig(null); });
    return () => { cancelled = true; };
  }, [user]);

  // Gestione mount/unmount basata su user, path e config.
  useEffect(() => {
    ensureHideStyle();
    const isExcluded = EXCLUDED_PATHS.some((p) => location.pathname.startsWith(p));
    const isUserRole = user && user.role && user.role !== "admin";
    const snippet = config?.active ? (config?.script || "") : "";
    if (isUserRole && !isExcluded && snippet) {
      addWidget(snippet);
    } else {
      removeWidget();
    }
  }, [user, location.pathname, config]);

  return null;
}
